/**
 * Patient-document type contracts (PROD-797).
 *
 * Mirrors Flutter `document_module/data/datamodels/patient_document_model.dart`.
 * The wire shape is shared by every document-tab consumer (the
 * appointment-panel Docs tab is the first; the patient-chart Docs tab
 * will follow).
 *
 * Field-level provenance: every field in this file maps 1:1 to a
 * `fromJson` line in the Dart source. Optional everywhere because the
 * Dart parser defaults missing values to `null` / `false` / `[]`.
 *
 * Audit fields (`createdById` / `createdByName` / `createdOn` /
 * `updatedById` / `updatedByName` / `updatedOn`) ship from the server
 * on read; on create the client ships them as `null` and the server
 * fills in the operator + timestamp.
 */

/**
 * Wire constants for `PatientDocumentModel.type`.
 *
 * **MANUAL SYNC SOURCE (Samer review PR #134 §4):**
 * Copy-pasted verbatim from Flutter
 * `scrips_msp1_flutter_shared/scrips_core/lib/constants/app_constants.dart`
 * lines 788-794 (as of 2026-05-14).
 *
 * These are wire-canonical strings the backend stores and queries on.
 * A rename on either side won't cause a TypeScript compile error — it
 * silently breaks document filtering, type-based rendering, and
 * sub-form dispatch. When updating this enum:
 *   1. Grep the Flutter source for `documentType` constants to
 *      confirm the new value.
 *   2. Update CONVERSION_NOTES_PROD-797.md "Manual sync points".
 *   3. Audit every consumer (filter widget, sub-form dispatcher,
 *      icon mapping in DocumentRow) for hardcoded references.
 *
 * DO NOT paraphrase the values themselves — "Admin & Referrals"
 * includes the ampersand; "Note" is singular.
 */
export const DocumentType = {
  Notes: 'Note',
  Forms: 'Form',
  Labs: 'Lab',
  Radiology: 'Radiology',
  Procedure: 'Procedure',
  AdminReferrals: 'Admin & Referrals',
} as const;

export type DocumentTypeCode = (typeof DocumentType)[keyof typeof DocumentType];

/**
 * Request body for `POST /api/Patients/GetPatientDocuments` (dev-curl
 * verified 2026-05-14 by Andrew). The Flutter `PatientDocumentRequestParam`
 * uses only `{ patientId, encounterId, pageNumber }` from the BLoC, but
 * the wire accepts all of the fields below — the Phase 2 filter widget
 * (PROD-XXX documents-filter-and-cleanup) unlocks `startDate` / `endDate`
 * / `fileType` / `uploadedBy` / `isArchived` + `pageSize` for paging.
 */
export interface GetPatientDocumentsRequest {
  patientId: string;
  /** Null when no encounter exists yet for this appointment. Backend
   *  returns all of the patient's documents in that case (verified). */
  encounterId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  /** Document-type filter (e.g. `[DocumentType.Labs, DocumentType.Notes]`). */
  fileType?: DocumentTypeCode[];
  /** Operator-id filter from the GetDocumentUploadedBy dropdown. */
  uploadedBy?: string[];
  pageNumber: number;
  /** Default 20 in Flutter. */
  pageSize?: number;
  /** Filter archived (soft-deleted) documents. Default false. */
  isArchived?: boolean;
}

/**
 * Flutter `class Data { title?: string; code?: string }` — used by
 * Procedure + Radiology document types to carry the picked CPT-style
 * code and a free-text title.
 */
export interface PatientDocumentData {
  title?: string | null;
  code?: string | null;
}

/**
 * Inner lab-identifier shape used inside `PatientDocumentLab.labName`.
 * Flutter parity: `class LabData { display?: string; code?: string }`
 * — wire field is `labName` (see PatientDocumentLab.toJson).
 *
 * **Required-key contract (Samer review PR #139 §1):** both `code` and
 * `display` are REQUIRED so that a PascalCase pass-through from a
 * master endpoint fails at compile time:
 *
 *   ```ts
 *   const row = { Code: 'x', Display: 'y' };  // master shape
 *   const lab: LabData = row;
 *   // ↑ TS Error: Property 'code' is missing
 *   ```
 *
 * The "not picked yet" state is the parent `labName: LabData | null`
 * branch — never a partial `LabData` literal.
 */
export interface LabData {
  display: string;
  code: string;
}

/**
 * Inner unit-identifier shape used inside `PatientDocumentLab.unitType`.
 * Flutter parity: `class UnitType { display?: string; code?: string }`.
 *
 * **Required-key contract (Samer review PR #139 §1):** see `LabData`
 * above. Both fields required so a PascalCase pass-through from the
 * master endpoint (`{ Code, Display }`) fails at compile time.
 */
export interface UnitType {
  display: string;
  code: string;
}

/**
 * Single lab-row child of a `PatientDocumentModel` when
 * `type === DocumentType.Labs`.
 *
 * Wire-shape gotcha: the field name on the JSON is `labName` (not
 * `labData`) — the Dart model renames it for readability but writes
 * `labName` on `toJson`. Mirror that on the React side.
 */
export interface PatientDocumentLab {
  id?: string | null;
  patientDocumentId?: string | null;
  /** Wire JSON key: `labName` (not `labData`). */
  labName?: LabData | null;
  result?: string | null;
  unitType?: UnitType | null;
  isDeleted?: boolean | null;
}

/**
 * Single attached file. Backend ships `{ id, patientDocumentId, url,
 * fileName, fileType, isDeleted }`. The React side keeps `fileBytesData`
 * + `frontImageBase64` for local-only upload-in-progress state, NEVER
 * sent on the wire (mirrors Flutter's `for local use only` markers).
 */
export interface PatientDocumentFile {
  id?: string | null;
  patientDocumentId?: string | null;
  url?: string | null;
  fileName?: string | null;
  fileType?: string | null;
  isDeleted?: boolean | null;

  /** Local-only — drag-drop buffer before upload. Not serialised. */
  fileBytesData?: ArrayBuffer | Uint8Array | null;
  /** Local-only — base64 preview for image-typed files before upload. */
  frontImageBase64?: string | null;
}

/**
 * Full patient-document record. Returned by GET /GetPatientDocuments
 * and ship-back from AddUpdatePatientDocument. Every field is optional
 * — the Dart parser defaults missing values to null / false / [].
 */
export interface PatientDocumentModel {
  id?: string | null;
  patientId?: string | null;
  encounterId?: string | null;
  /**
   * The appointment this document belongs to — the WRITE half of the
   * session↔report link (PROD-2374).
   *
   * `POST /api/Patients/GetDocumentsByAppointments` filters on
   * `PatientDocument.AppointmentId`, so a document saved WITHOUT this field
   * can never be read back by that endpoint: the column stays NULL and the
   * Technician Worklist says "no report" for a session that has one. Every
   * surface that files a document against a specific appointment must send
   * it; the surfaces that file against the patient (or an encounter) send
   * `null`, which is what the column held for every document written before
   * this ticket.
   *
   * Server-side this maps to `PatientDocumentsDto.AppointmentId` in
   * Scrips.Patient — already present, already mapped (AutoMapper does not
   * ignore it), column + filtered index already applied. Nothing on the
   * backend needs changing; this is purely the client learning to send it.
   */
  appointmentId?: string | null;
  name?: string | null;
  type?: DocumentTypeCode | string | null;
  /** ISO datetime, no-Z (.NET model binder rejects Z suffix). */
  documentDate?: string | null;
  doctorName?: string | null;
  facilityName?: string | null;
  tags?: string | null;
  reviewedById?: string | null;
  reviewedByName?: string | null;
  /** ISO datetime, no-Z. */
  reviewedOn?: string | null;
  /** Used by Procedure / Radiology types. */
  data?: PatientDocumentData | null;

  // Audit fields — server-set on create; mirror back on read.
  createdById?: string | null;
  createdByName?: string | null;
  createdOn?: string | null;
  updatedById?: string | null;
  updatedByName?: string | null;
  updatedOn?: string | null;

  /** Soft-delete / archive flag on the document record. */
  isDeleted?: boolean | null;

  patientDocumentFiles?: PatientDocumentFile[];
  patientDocumentLabs?: PatientDocumentLab[];
}

/**
 * Operator entry returned by GET /api/Patients/GetDocumentUploadedBy.
 * Used by the Phase 2 filter widget (PROD-XXX documents-filter-and-cleanup)
 * to scope the list to specific uploaders. Phase 1 ships the
 * `useDocumentUploadedBy` query hook.
 *
 * Wire JSON keys: `userId` + `name` (Flutter `uploaded_by_data.dart:29-37`
 * — `fromJson` reads `json["userId"]`, `toJson` writes `"userId"`; the
 * Dart class internally renames the property to `uploadedById` but the
 * wire field is `userId`). The previous `id` field on this interface
 * was wrong-name drift — would silently deserialise to `undefined`.
 */
export interface UploadedByData {
  /** Wire JSON key: `userId` (NOT `id`). */
  userId?: string | null;
  /** Operator's display name. */
  name?: string | null;
}
